import { useEffect, useMemo, useRef, useState } from 'react';
import type { Broadcast, KhutbahColours, KhutbahSettings } from '@noorview/core';
import { KHUTBAH_ADDON, KHUTBAH_LOOKS, KHUTBAH_SWATCHES, UNDERLINE_SWATCHES, hhmm, isBroadcastLive, normalizeKhutbah } from '@noorview/core';
import type { Stage } from '@noorview/display';
import { attempt, backend, toast, useApp } from '../data/store';
import { ColourChoice, Segmented, Sheet, Switch } from '../ui/controls';
import { Select } from '../ui/pickers';
import { Icon } from '../ui/icons';
import { StageView } from '../ui/StageView';
import { blankCanvas } from '../app/designs';

/**
 * Live Khutbah (an add-on): start and stop it, choose the screen that listens and the screens that show it, its
 * colours, and whether it starts by itself at Jumu'ah. The look itself is fixed; the preview plays a sample khutbah
 * in the chosen colours, or shows the real one while it's on.
 */

const LENGTHS: Array<[number, string]> = [[30, '30 minutes'], [45, '45 minutes'], [60, '1 hour'], [90, '1½ hours'], [120, '2 hours']];
const live = (bcs: readonly Broadcast[]) => bcs.find((b) => b.kind === 'khutbah' && isBroadcastLive(b, new Date())) ?? null;

export function KhutbahSheet({ onClose }: { onClose: () => void }) {
  const ws = useApp((s) => s.ws)!;
  return ws.masjid.khutbahAddon ? <Manage onClose={onClose} /> : <AddOn onClose={onClose} />;
}

/** Without the add-on: what it is, and how to get it. */
function AddOn({ onClose }: { onClose: () => void }) {
  return (
    <Sheet open onClose={onClose} title="Live Khutbah" subtitle="An add-on for any plan." size="wide"
      footer={<><button type="button" className="btn plain" onClick={onClose}>Not now</button><a className="btn primary" href="https://www.noormsystems.co.uk/#contact" target="_blank" rel="noopener">Talk to us about adding it</a></>}>
      <Preview colours={KHUTBAH_LOOKS[0]!.colours} />
      <div className="plan-c ctl kh-addon">
        <b>{KHUTBAH_ADDON.name}</b>
        <div className="price"><span>{KHUTBAH_ADDON.price}</span><small>{KHUTBAH_ADDON.note}</small></div>
        <ul>{KHUTBAH_ADDON.feats.map((f) => <li key={f}><Icon name="check" size={16} />{f}</li>)}</ul>
      </div>
    </Sheet>
  );
}

function Manage({ onClose }: { onClose: () => void }) {
  const ws = useApp((s) => s.ws)!;
  const tz = ws.masjid.timezone;
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 5000); return () => clearInterval(t); }, []);
  const on = live(ws.broadcasts);
  const [k, setK] = useState<KhutbahSettings>(() => normalizeKhutbah(ws.masjid.khutbah));
  const [minutes, setMinutes] = useState(60);
  const [busy, setBusy] = useState(false);
  // Each change is saved a moment after the last one (and reaches a khutbah that's on straight away).
  const saving = useRef<ReturnType<typeof setTimeout> | null>(null);
  const change = (patch: Partial<KhutbahSettings>) => {
    const next = { ...k, ...patch };
    setK(next);
    if (saving.current) clearTimeout(saving.current);
    saving.current = setTimeout(() => { saving.current = null; void attempt(() => backend.saveKhutbah(next)); }, 500);
  };
  const latestK = useRef(k);
  latestK.current = k;
  // Closing straight after a change still saves it.
  useEffect(() => () => { if (saving.current) { clearTimeout(saving.current); void attempt(() => backend.saveKhutbah(latestK.current)); } }, []);
  const colour = (key: keyof KhutbahColours) => (v: string | null) => v && change({ colours: { ...k.colours, [key]: v } });
  const look = KHUTBAH_LOOKS.find((l) => JSON.stringify(l.colours) === JSON.stringify(k.colours))?.id ?? 'custom';
  const listener = ws.screens.find((s) => s.id === k.listenerScreenId) ?? null;

  const start = async () => {
    if (saving.current) { clearTimeout(saving.current); saving.current = null; await backend.saveKhutbah(k).catch(() => undefined); }
    if (!listener) { toast('Choose the screen that listens to the khutbah first.', { bad: true }); return; }
    setBusy(true);
    await attempt(() => backend.startKhutbah(minutes), 'Live khutbah started');
    setBusy(false);
  };
  const stop = async () => { setBusy(true); await attempt(() => backend.stopKhutbah(), 'Live khutbah stopped'); setBusy(false); };

  return (
    <Sheet open onClose={onClose} title="Live Khutbah" subtitle="The khutbah in English, live, on your screens." size="wide" dock
      footer={<>
        <button type="button" className="btn plain" onClick={onClose}>Done</button>
        {on
          ? <button type="button" className="btn danger" disabled={busy} onClick={stop}>{busy ? 'Stopping…' : 'Stop live khutbah'}</button>
          : <button type="button" className="btn primary" disabled={busy} onClick={start}>{busy ? 'Starting…' : 'Start live khutbah'}</button>}
      </>}>
      <div className={`kh-status ctl${on ? ' on' : ''}`} role="status">
        <span className="kh-state">{on ? 'On' : 'Off'}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <b>{on ? 'On the screens now' : 'Not on'}</b>
          <small className="hint" style={{ display: 'block' }}>{on
            ? `${on.khutbah?.startedBy ? `Started by ${on.khutbah.startedBy} · ` : ''}stops by itself at ${hhmm(new Date(on.endsAt!), tz)}`
            : 'It covers the whole screen, even when the screens are blank for salah, and goes back to normal when it’s stopped.'}</small>
        </span>
      </div>
      <Preview colours={k.colours} live={on} />
      <div className="two">
        <div className="field"><span>Screen that listens</span>
          <Select label="Screen that listens" value={k.listenerScreenId ?? ''} placeholder="Choose a screen" onChange={(v) => change({ listenerScreenId: v || null })}
            options={ws.screens.map((s) => ({ value: s.id, label: `${s.name}${s.deviceId ? '' : ' (not paired)'}` }))} />
          <small className="hint">Its microphone, or a mixer plugged into it, hears the imam. A PC works best.</small>
        </div>
        {!on && <div className="field"><span>Stops by itself after</span><Select label="Stops by itself after" value={String(minutes)} onChange={(v) => setMinutes(Number(v))} options={LENGTHS.map(([v, l]) => ({ value: String(v), label: l }))} /></div>}
      </div>
      <div className="field"><span>Show it on</span>
        <Segmented size="sm" label="Show it on" value={k.screenIds ? 'some' : 'all'} onChange={(v) => change({ screenIds: v === 'all' ? null : (k.screenIds ?? ws.screens.slice(0, 1).map((s) => s.id)) })}
          options={[{ value: 'all', label: 'All screens' }, { value: 'some', label: 'Choose screens' }]} />
        {k.screenIds && (
          <div className="bc-screens" role="group" aria-label="Choose screens">
            {ws.screens.map((s) => {
              const sel = k.screenIds!.includes(s.id);
              return <button key={s.id} type="button" className={`cat${sel ? ' on' : ''}`} aria-pressed={sel} onClick={() => change({ screenIds: sel ? k.screenIds!.filter((x) => x !== s.id) : [...k.screenIds!, s.id] })}>{sel && <Icon name="check" size={14} stroke={3} />}{s.name}</button>;
            })}
          </div>
        )}
      </div>
      <div className="toggle-row">
        <div><span className="label">Start by itself at Jumu‘ah</span><small className="hint">From {k.auto.before} minute{k.auto.before === 1 ? '' : 's'} before each khutbah time, until the jama‘ah starts.</small></div>
        <Switch label="Start by itself at Jumu‘ah" checked={k.auto.enabled} onChange={(v) => change({ auto: { ...k.auto, enabled: v } })} />
      </div>
      {k.auto.enabled && (
        <div className="field"><span>Start it</span>
          <Segmented size="sm" label="Start it" value={String(k.auto.before)} onChange={(v) => change({ auto: { ...k.auto, before: Number(v) } })}
            options={[0, 2, 5, 10].map((m) => ({ value: String(m), label: m ? `${m} min before` : 'At the time' }))} />
        </div>
      )}
      <div className="section-label">Colours</div>
      <Segmented size="sm" label="Look" value={look} onChange={(v) => { const l = KHUTBAH_LOOKS.find((x) => x.id === v); if (l) change({ colours: { ...l.colours } }); }}
        options={[...KHUTBAH_LOOKS.map((l) => ({ value: l.id, label: l.label })), ...(look === 'custom' ? [{ value: 'custom', label: 'Your colours' }] : [])]} />
      <div className="kh-colours">
        <div className="field"><span>Background</span><ColourChoice label="Background" value={k.colours.background} swatches={KHUTBAH_SWATCHES} onChange={colour('background')} /></div>
        <div className="field"><span>Not heard clearly</span><ColourChoice label="Not heard clearly" value={k.colours.unclear} swatches={UNDERLINE_SWATCHES} onChange={colour('unclear')} /></div>
        <div className="field"><span>Qur’an</span><ColourChoice label="Qur’an" value={k.colours.quran} swatches={UNDERLINE_SWATCHES} onChange={colour('quran')} /></div>
        <div className="field"><span>Hadith</span><ColourChoice label="Hadith" value={k.colours.hadith} swatches={UNDERLINE_SWATCHES} onChange={colour('hadith')} /></div>
      </div>
    </Sheet>
  );
}

/** The khutbah as the screens show it: the sample in these colours, or the real one while it's on. */
function Preview({ colours, live: on }: { colours: KhutbahColours; live?: Broadcast | null }) {
  const ws = useApp((s) => s.ws)!;
  const design = useMemo(() => blankCanvas(), []);
  const stage = useRef<Stage | null>(null);
  const bcs = useMemo(() => (on ? [{ ...on, screenIds: null, khutbah: { ...on.khutbah!, colours } }] : []), [on, colours]);
  useEffect(() => { if (!on) stage.current?.setKhutbahColours(colours); }, [colours, on]);
  return (
    <div className="kh-preview">
      <StageView key={on ? on.id : 'sample'} design={design} masjid={ws.masjid} timetable={ws.timetable} mode="screen" salahBlank={false} defer={false} broadcasts={bcs}
        onStage={(s) => { stage.current = s; if (s && !on) s.playKhutbahSample(colours); }} className="stage-fill" label={on ? 'The live khutbah, as the screens show it' : 'A sample of how the live khutbah looks'} />
      {!on && <span className="kh-sample">Sample</span>}
    </div>
  );
}
